import { useMutation } from '@apollo/client';
import { AddCircle, Info, RemoveCircle } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
  styled,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '~/api/helpers';
import { useDeletePlaylistItem } from '~/api/playlist-items';
import { useDeletePlaylist } from '~/api/playlists';
import { useCreateViewZonePlaylist, useDeleteViewZonePlaylist } from '~/api/view-zone-playlists';
import { InfoTooltip } from '~/components/Tooltip/InfoTooltip';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { LayoutSkeleton } from '~/components/layout-skeleton';
import { useConfirmDialog } from '~/hooks/dialogs';
import AddPlaylistContentIllustration from '~/images/illustrations/playlist-content.svg';
import { assert } from '~/lib/assert';
import { UpdateViewZoneDocument } from '../../queries/queries.generated';
import { ContentAddButton } from './ContentAddButton';
import { PlaylistSelectDialog } from './PlaylistSelectDialog';
import type {
  ShowViewZone__Layout as Layout,
  ShowViewZone__Playlist as Playlist,
  ShowViewZone__Show as Show,
  ShowViewZone__ViewZone as ViewZoneFragment,
} from './ViewZone.generated';
import { ViewZonePlaylist } from './ViewZonePlaylist';

/* GraphQL */ `#graphql
fragment ShowViewZone__Layout on Layout {
  ...LayoutSkeleton__Layout
}

fragment ShowViewZone__Playlist on Playlist {
  ...PlaylistDesign__Playlist
}

fragment ShowViewZone__Show on Show {
  ...ContentAdd__Show
}

fragment ShowViewZone__ViewZone on ViewZone {
  id
  name
  reportPlayback
  sync
  viewZonePlaylists {
    id
    playlist {
      ...ShowViewZone__Playlist
    }
  }
  zoneId
}
`;

const ZoneHeading = ({ layout, multiZone, viewZone }: Omit<ViewZoneProps, 'show'>) => {
  const { t } = useTranslation();

  if (!multiZone)
    return (
      <Typography variant="h4">{t('pages.shows.detail.view_zone.content_heading')}</Typography>
    );

  const zone = layout.zones.find((zone) => zone.id === viewZone.zoneId);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      <Typography variant="h4">{viewZone.name}</Typography>
      <Typography
        variant="caption"
        sx={{ ml: 1, mb: '3px' }}
      >{`(${zone?.width}x${zone?.height})`}</Typography>
    </Box>
  );
};

export interface ViewZoneProps {
  id?: string;
  layout: Layout;
  multiZone: boolean;
  show: Show;
  viewZone: ViewZoneFragment;
}

const HelpIcon = styled(Info)(({ theme }) => ({
  color: theme.palette.secondary.main,
  marginRight: theme.spacing(0.5),
}));

export const ViewZone = ({ id, layout, multiZone, show, viewZone }: ViewZoneProps) => {
  const { t } = useTranslation();
  const viewZonePlaylists = viewZone.viewZonePlaylists;
  const [activePlaylistId, setActivePlaylistId] = useState<number | null>(
    viewZonePlaylists[0]?.id ?? null,
  );
  const showTabs = useMemo(
    () =>
      activePlaylistId != null &&
      (viewZonePlaylists.length > 1 || viewZonePlaylists[0]?.playlist.reusable),
    [activePlaylistId, viewZonePlaylists],
  );

  useEffect(() => {
    if (!viewZonePlaylists.length) {
      setActivePlaylistId(null);
      return;
    }

    if (!viewZonePlaylists.some((vzp) => vzp.id === activePlaylistId)) {
      setActivePlaylistId(viewZonePlaylists[0]?.id ?? null);
    }
  }, [activePlaylistId, viewZonePlaylists]);

  // Track selected items for the currently visible playlist
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [removePlaylistConfirm, removePlaylistProps] = useConfirmDialog();
  const [confirmBulkItemsDelete, confirmBulkItemsDeleteProps] = useConfirmDialog();
  const [confirmSingleItemDelete, confirmSingleItemDeleteProps] = useConfirmDialog();
  const [addPlaylistDialogOpen, setAddPlaylistDialogOpen] = useState(false);

  const [deletePlaylistItem] = useDeletePlaylistItem();
  const [deletePlaylist] = useDeletePlaylist();
  const [removeViewZonePlaylist] = useDeleteViewZonePlaylist();
  const [createViewZonePlaylist, createViewZonePlaylistState] = useCreateViewZonePlaylist();

  const zone = useMemo(
    () => layout.zones.find((x) => viewZone.zoneId === x.id),
    [layout.zones, viewZone.zoneId],
  );
  assert(!!zone, 'zone not found');

  const [updateViewZone, updateViewZoneState] = useMutation(
    UpdateViewZoneDocument,
    useNotifications(UpdateViewZoneDocument, { success: 'Updated zone' }),
  );

  // Helper to remove the view zone playlist when a reusable playlist has been selected.
  const removePlaylist = useCallback(
    async (viewZonePlaylistId: number) => {
      if (!(await removePlaylistConfirm())) return;
      await removeViewZonePlaylist({
        variables: { viewZonePlaylistId },
      });
    },
    [removePlaylistConfirm, removeViewZonePlaylist],
  );

  // Helper used to delete a playlist if all items were selected, otherwise the items will be deleted.
  const handlePlaylistDelete = useCallback(
    async (playlist: { id: number; reusable: boolean }, itemIds: number[], all: boolean) => {
      assert(!playlist.reusable, 'Cannot use this for a non-reusable playlist');

      if (all) {
        await deletePlaylist({ variables: { playlistIds: [playlist.id] } });
      } else {
        await deletePlaylistItem({
          variables: {
            playlistId: playlist.id,
            playlistItemIds: itemIds,
          },
        });
      }
    },
    [deletePlaylist, deletePlaylistItem],
  );

  // Helper to remove a single playlist item.
  const removeSinglePlaylistItem = useCallback(
    async (playlist: Playlist, playlistItemId: number) => {
      assert(!playlist.reusable, 'Cannot remove content from a reusable playlist');

      if (!(await confirmSingleItemDelete())) return;

      await handlePlaylistDelete(playlist, [playlistItemId], playlist.playlistItems.length === 1);
    },
    [confirmSingleItemDelete, handlePlaylistDelete],
  );

  // Helper to remove bulk playlist items.
  const removeBulkPlaylistItems = useCallback(
    async (playlist: Playlist) => {
      assert(!playlist.reusable, 'Cannot remove content from a reusable playlist');

      // Get out if no items are selected
      if (selectedIds.length === 0) return;

      if (!(await confirmBulkItemsDelete())) return;

      // If deleting all items then delete the playlist which will delete the items as well
      await handlePlaylistDelete(
        playlist,
        selectedIds.map((id) => +id),
        playlist.playlistItems.length === selectedIds.length,
      );

      // Reset selected items
      setSelectedIds([]);
    },
    [confirmBulkItemsDelete, handlePlaylistDelete, selectedIds],
  );

  const reportingDisabled = useMemo(
    () => updateViewZoneState.loading || viewZonePlaylists.length === 0,
    [updateViewZoneState.loading, viewZonePlaylists],
  );

  const reportingSwitch = (
    <Switch
      checked={viewZone.reportPlayback}
      disabled={reportingDisabled}
      onChange={(event) =>
        updateViewZone({
          variables: { viewZoneId: viewZone.id, patch: { reportPlayback: event.target.checked } },
        })
      }
      size="small"
    />
  );

  const handleAddPlaylist = useCallback(
    async (selectedPlaylistId: number | null) => {
      if (selectedPlaylistId == null) return;

      await createViewZonePlaylist({
        variables: {
          input: {
            playlistId: selectedPlaylistId,
            viewZoneId: viewZone.id,
          },
        },
      });
    },
    [createViewZonePlaylist, viewZone.id],
  );

  const syncDisabled = useMemo(
    () =>
      updateViewZoneState.loading ||
      viewZonePlaylists.length === 0 ||
      viewZonePlaylists
        .filter((vzp) => vzp.playlist.kind !== 'ad')
        .some((vzp) => vzp.playlist.playlistItems.some((item) => !item.duration)),
    [updateViewZoneState.loading, viewZonePlaylists],
  );

  const syncSwitch = (
    <Switch
      checked={viewZone.sync}
      disabled={syncDisabled}
      onChange={(event) =>
        updateViewZone({
          variables: { viewZoneId: viewZone.id, patch: { sync: event.target.checked } },
        })
      }
      size="small"
    />
  );

  return (
    <Paper id={id} sx={{ mt: 2 }}>
      <Stack
        direction={{ sm: 'column', md: 'row' }}
        spacing={{ sm: 1, md: 2 }}
        alignItems={{ md: 'flex-start', lg: 'center' }}
        justifyContent="space-between"
        sx={{ p: 2 }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 2 }}>
          {multiZone && (
            <Box sx={{ width: '150px', border: 1, borderColor: 'borders.light' }}>
              <LayoutSkeleton layout={layout} showOnlySelected zoneId={viewZone.zoneId} />
            </Box>
          )}
          <Box>
            <ZoneHeading layout={layout} viewZone={viewZone} multiZone={multiZone} />
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {t('pages.shows.detail.view_zone.add_content_description')}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ ml: 0.5, mt: 0.75, alignItems: { sm: 'center' } }}
            >
              <Box sx={{ alignItems: 'center', display: 'flex' }}>
                <FormControlLabel
                  control={reportingSwitch}
                  label={t('pages.shows.detail.view_zone.enable_reporting_label')}
                  sx={{ alignItems: 'baseline', mr: 0.5 }}
                />
                <InfoTooltip
                  icon={<HelpIcon fontSize="small" />}
                  title={
                    reportingDisabled
                      ? t('pages.shows.detail.view_zone.reporting_disabled_tooltip')
                      : t('pages.shows.detail.view_zone.reporting_tooltip')
                  }
                />
              </Box>
              <Box sx={{ alignItems: 'center', display: 'flex' }}>
                <FormControlLabel
                  control={syncSwitch}
                  label={t('pages.shows.detail.view_zone.enable_sync_label')}
                  sx={{ alignItems: 'baseline', mr: 0.5 }}
                />
                <InfoTooltip
                  icon={<HelpIcon fontSize="small" />}
                  title={
                    syncDisabled
                      ? t('pages.shows.detail.view_zone.sync_disabled_tooltip')
                      : t('pages.shows.detail.view_zone.sync_tooltip')
                  }
                />
              </Box>
            </Stack>
          </Box>
        </Box>
        {viewZonePlaylists.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button
              color="primary"
              startIcon={<AddCircle />}
              variant="contained"
              onClick={() => setAddPlaylistDialogOpen(true)}
              disabled={createViewZonePlaylistState.loading}
            >
              {t('pages.shows.detail.view_zone.add_playlist_button')}
            </Button>
          </Box>
        )}
      </Stack>
      {showTabs && (
        <Tabs
          value={activePlaylistId}
          onChange={(_event, newValue: number) => {
            setActivePlaylistId(newValue);
            setSelectedIds([]); // Clear selection when switching tabs
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            backgroundColor: 'white',
            borderTop: (theme) => `1px solid ${theme.palette.borders.light}`,
            borderBottom: 0,
          }}
        >
          {viewZonePlaylists.map((viewZonePlaylist, index) => {
            const { playlist } = viewZonePlaylist;
            const label = playlist.reusable
              ? playlist.name
              : t('pages.shows.detail.view_zone.playlist_label', { index: index + 1 });
            // A tab may hold no control of its own; a shared playlist's edit link sits in the
            // panel toolbar instead (see ViewZonePlaylist).
            const tabLabel = playlist.reusable ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {label}
                <Chip
                  color="primary"
                  label={t('pages.shows.detail.view_zone.shared_badge')}
                  size="small"
                  sx={{ ml: 1, fontSize: '0.75rem', letterSpacing: 0, textTransform: 'none' }}
                />
              </Box>
            ) : (
              label
            );
            return <Tab key={viewZonePlaylist.id} label={tabLabel} value={viewZonePlaylist.id} />;
          })}
        </Tabs>
      )}
      {viewZonePlaylists.length === 0 ? (
        <Box
          sx={(theme) => ({
            display: 'grid',
            placeItems: 'center',
            px: 2,
            py: 6,
            backgroundColor: theme.palette.background.default,
          })}
        >
          <EmptyState
            illustration={AddPlaylistContentIllustration}
            header={t('pages.shows.detail.view_zone.empty_state_header')}
            primaryAction={<ContentAddButton show={show} viewZone={viewZone} zoneFilter={zone} />}
          />
        </Box>
      ) : (
        (showTabs
          ? viewZonePlaylists.filter((vzp) => vzp.id === activePlaylistId)
          : viewZonePlaylists
        ).map((viewZonePlaylist) => {
          const playlist = viewZonePlaylist.playlist;

          return (
            <ViewZonePlaylist
              key={viewZonePlaylist.id}
              onRemoveBulk={() => void removeBulkPlaylistItems(playlist)}
              onRemovePlaylist={removePlaylist}
              onRemovePlaylistItem={(playlistItemId) =>
                void removeSinglePlaylistItem(playlist, playlistItemId)
              }
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              show={show}
              viewZone={viewZone}
              viewZonePlaylist={{ id: viewZonePlaylist.id, playlist: viewZonePlaylist.playlist }}
              zone={zone}
            />
          );
        })
      )}
      <PlaylistSelectDialog
        open={addPlaylistDialogOpen}
        onClose={() => setAddPlaylistDialogOpen(false)}
        onChange={(playlistId) => {
          setAddPlaylistDialogOpen(false);
          void handleAddPlaylist(playlistId);
        }}
        zoneFilter={zone}
      />
      <ConfirmDialog
        {...confirmSingleItemDeleteProps}
        cancel={t('common.dialogs.cancel')}
        confirm={t('common.dialogs.remove')}
        deleteConfirm
        deleteIcon={<RemoveCircle />}
        prompt={t('pages.shows.detail.view_zone.remove_single_content_prompt')}
        title={t('pages.shows.detail.view_zone.remove_content_title')}
      />
      <ConfirmDialog
        {...confirmBulkItemsDeleteProps}
        cancel={t('common.dialogs.cancel')}
        confirm={t('common.dialogs.remove')}
        deleteConfirm
        deleteIcon={<RemoveCircle />}
        prompt={t('pages.shows.detail.view_zone.remove_bulk_content_prompt')}
        title={t('pages.shows.detail.view_zone.remove_content_title')}
      />
      <ConfirmDialog
        {...removePlaylistProps}
        cancel={t('common.dialogs.cancel')}
        confirm={t('common.dialogs.remove')}
        deleteConfirm
        deleteIcon={<RemoveCircle />}
        prompt={t('pages.shows.detail.view_zone.remove_playlist_prompt')}
        title={t('pages.shows.detail.view_zone.remove_playlist_title')}
      />
    </Paper>
  );
};
